/**
 * Service-worker Background Sync hand-off.
 *
 * The Background Sync API (`ServiceWorkerRegistration.sync`) lets the browser
 * replay the outbox even after every tab is closed: register a one-off sync
 * tag and the SW's `sync` event fires when connectivity returns. We feature-
 * detect every layer (SW support, registration, `sync` manager) and degrade
 * to the in-page poll loop when any layer is missing (Safari, Firefox without
 * the flag, insecure context, SSR).
 *
 * This module owns only the *registration* side. The SW script itself (the
 * file at `serviceWorkerUrl`) is shipped by the consuming app; the README
 * documents the contract it must implement against the same batch endpoint.
 */

import { validateRegisterBackgroundSyncArgs } from './validate-input'

interface SyncManagerLike {
	register(tag: string): Promise<void>
}

interface RegistrationLike {
	sync?: SyncManagerLike
}

interface ServiceWorkerContainerLike {
	register(url: string): Promise<RegistrationLike>
	ready: Promise<RegistrationLike>
}

function getContainer(): ServiceWorkerContainerLike | null {
	const nav = (
		globalThis as {
			navigator?: { serviceWorker?: ServiceWorkerContainerLike }
		}
	).navigator
	return nav?.serviceWorker ?? null
}

/** True if SW + Background Sync are usable in this environment. */
export function isBackgroundSyncSupported(): boolean {
	const container = getContainer()
	if (!container) return false
	// `SyncManager` global presence is the standard capability probe.
	return 'SyncManager' in (globalThis as Record<string, unknown>)
}

/**
 * Register the service worker and request a one-off Background Sync.
 *
 * @returns `true` when Background Sync was successfully requested, `false`
 *          when the environment forced a fallback to the in-page poll loop.
 */
export async function registerBackgroundSync(
	serviceWorkerUrl: string,
	tag: string,
): Promise<boolean> {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	validateRegisterBackgroundSyncArgs(serviceWorkerUrl, tag)
	const container = getContainer()
	if (!container) return false
	try {
		await container.register(serviceWorkerUrl)
		const reg = await container.ready
		if (!reg.sync) return false
		await reg.sync.register(tag)
		return true
	} catch {
		// Any failure (insecure context, quota, SW parse error) → fallback.
		return false
	}
}
